import { memo } from 'react';

import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

const workspaceAvatarContainerVariants = tailwindVariants({
  base: 'relative h-8 min-w-[32px] overflow-hidden rounded-4',
  variants: {
    size: {
      medium: 'h-8 w-8 min-w-[32px]',
      large: 'h-10 w-10 min-w-[40px]',
      extraLarge: 'h-14 w-14 min-w-[56px]',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

const workspaceAvatarVariants = tailwindVariants({
  base: 'h-full object-cover',
  variants: {
    size: {
      medium: 'w-8',
      large: 'w-10',
      extraLarge: 'w-14',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

const workspaceAvatarNameVariants = tailwindVariants({
  base: 'ml-3 truncate font-medium ads:text-metadata text-gray-10',
  variants: {
    size: {
      medium: 'ml-3 text-16',
      large: 'ml-4 text-16',
      extraLarge: 'ml-4 text-16',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

type WorkspaceAvatarAndNameVariants = VariantProps<typeof workspaceAvatarContainerVariants>;

export type WorkspaceAvatarAndNameProps = {
  image: string;
  name: string;

  /**
   * @default medium
   */
  size?: WorkspaceAvatarAndNameVariants['size'];
  className?: string;
};

export const WorkspaceAvatar = memo(({ image, name, size = 'medium' }: WorkspaceAvatarAndNameProps) => {
  return (
    <div className={workspaceAvatarContainerVariants({ size })} data-testid="WORKSPACE_AVATAR">
      {/* biome-ignore lint/performance/noImgElement: workspace logo URL is dynamic; Next Image not used here */}
      <img alt={`Logo for ${name}`} className={workspaceAvatarVariants({ size })} src={image} />
    </div>
  );
});
WorkspaceAvatar.displayName = 'WorkspaceAvatar';

export const WorkspaceAvatarAndName = memo(
  ({ name, image, size = 'medium', className }: WorkspaceAvatarAndNameProps) => {
    return (
      <div className={cn('flex items-center', className)} data-testid="WORKSPACE_AVATAR_AND_NAME">
        <WorkspaceAvatar image={image} name={name} size={size} />
        <div className={workspaceAvatarNameVariants({ size })} data-testid="WORKSPACE_NAME">
          {name}
        </div>
      </div>
    );
  },
);
WorkspaceAvatarAndName.displayName = 'WorkspaceAvatarAndName';
